import { createContext, useContext, ReactNode } from "react";
import { PermissionGridColumns, usePermissionColumns } from "./usePermissionColumns.tsx";
import { Permission, useGetAllPermissions } from "src/api/client-api/permissions/useGetAllPermissions";
import { useGridColumns, GridColumnsType } from "src/components/Grid/hooks/columnsExtension/useGridColumns";
import { DelayedSearch, useDelayedSearch } from "src/utils/useDelayedSearch.tsx";
import { useUrlToApi } from "src/api/_common/params/useUrlToApi";
import { MenuItemType } from "src/components/ActionsPopover/ActionsPopover.types";
import { AppSearch } from "src/api/_common/hydra/search";
import { ApiPagination } from "src/api/_common/params/useApiPaginationUrlSync";
import { usePermissionsMenuItems } from "src/pages/Settings/Permissions/usePermissionMenuItems.tsx";

type PermissionListContextInitialValues = {
    mrt_columns: PermissionGridColumns;
    gridConfiguration: GridColumnsType<Permission>;

    totalItems: number;
    permissions: Permission[];
    isRefetching: boolean;
    isReady: boolean;
    menuItems: MenuItemType<Permission>[];

    globalSearch: DelayedSearch;
    appSearch: AppSearch | undefined;

    gridKey: string;
    pagination: ApiPagination;
};
const PermissionListContext = createContext<PermissionListContextInitialValues | null>(null);

type PermissionListContextProviderProps = {
    children: ReactNode;
    onEditPermission: (permission: Permission) => void;
};
export const PermissionListContextProvider = (props: PermissionListContextProviderProps) => {
    const { children, onEditPermission } = props;

    const globalSearch = useDelayedSearch();
    const { allAsAxiosParams, pagination } = useUrlToApi({ page: "1", itemsPerPage: "20" });
    const { permissions, isReady, appSearch, totalItems, isRefetching } = useGetAllPermissions({
        cacheTime: 0,
        search: globalSearch.delayedSearch,
        params: {
            ...allAsAxiosParams,
        },
        // sorting: sorting,
    });

    const gridKey = "permission-grid";

    const mrt_permissionColumns = usePermissionColumns(permissions ?? [], gridKey);
    const gridConfiguration = useGridColumns(mrt_permissionColumns, gridKey);
    const menuItems = usePermissionsMenuItems(onEditPermission);

    const items: PermissionListContextInitialValues = {
        mrt_columns: mrt_permissionColumns,
        gridConfiguration,

        totalItems: totalItems ?? 0,
        permissions: permissions ?? [],
        isReady,
        isRefetching,
        menuItems,
        appSearch,
        globalSearch,

        gridKey: gridKey,
        pagination,
    };

    return <PermissionListContext.Provider value={items}>{children}</PermissionListContext.Provider>;
};

export const usePermissionListContext = () => {
    const context = useContext(PermissionListContext);

    if (context) {
        return context;
    }

    throw new Error(`usePermissionListContext must be used within an PermissionListContextProvider`);
};
